import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Map, MessageSquareQuote } from "lucide-react";
import { mapQuickAccessHref, isMapRoutePath } from "./map/MapQuickAccess";
import { piazzaVistaMappaPath } from "./piazza/PiazzaMobileMapVista";
import { trackTerritoryEngagement } from "../utils/analytics";

const OPEN_AMBASC_EVENT = "6amico:open-ambasciatore";

export function openAmbasciatoreChat() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_AMBASC_EVENT));
}

export { OPEN_AMBASC_EVENT };

const MAP_VARIANTS = [
  {
    id: "montagna",
    label: "Montagna",
    emoji: "⛰️",
    href: "/esplora-la-mappa?mode=terra&macro=montagna",
  },
  {
    id: "terra",
    label: "Terra e Borghi",
    emoji: "🌿",
    href: "/esplora-la-mappa?mode=terra&macro=terra",
  },
  {
    id: "mare",
    label: "Mare e Costa",
    emoji: "🌊",
    href: "/esplora-la-mappa?mode=mare",
  },
];

/**
 * Mappa + Ambasciatore nella navbar (blocco destro, prima della lingua).
 * Desktop: pulsanti compatti testo+icona. Mobile: icone compatte.
 * Su /piazze mobile: «Apri Mappa» attiva la vista in-page (?vista=mappa).
 */
export default function HeaderMobileQuickActions() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [mapMenuOpen, setMapMenuOpen] = useState(false);

  const primaryHref = useMemo(
    () => mapQuickAccessHref(pathname, search),
    [pathname, search],
  );

  const onMapRoutes = isMapRoutePath(pathname);
  const onPiazze =
    pathname === "/piazze" || pathname === "/piazza" || pathname.startsWith("/piazze/");

  const openPiazzaMapVista = () => {
    setMapMenuOpen(false);
    navigate(piazzaVistaMappaPath(search));
    trackTerritoryEngagement({
      section: "mappa",
      source: "header_mobile_piazza_vista",
    });
  };

  return (
    <div className="header-nav-quick" data-testid="header-mobile-quick">
      {!onMapRoutes && (
        <div className="header-nav-quick__map-wrap relative">
          <Link
            to={primaryHref}
            className="header-nav-quick__btn header-nav-quick__btn--desktop"
            data-testid="header-apri-mappa-desktop"
            aria-label="Apri Mappa"
            title="Apri Mappa"
            onClick={() =>
              trackTerritoryEngagement({
                section: "mappa",
                source: "header_nav_desktop",
              })
            }
          >
            <Map size={14} strokeWidth={2} aria-hidden />
            <span>Apri Mappa</span>
          </Link>

          <button
            type="button"
            className="header-nav-quick__btn header-nav-quick__btn--mobile"
            data-testid="header-apri-mappa-btn"
            aria-label="Apri Mappa"
            aria-expanded={onPiazze ? undefined : mapMenuOpen}
            title="Apri Mappa"
            onClick={() => {
              if (onPiazze) {
                openPiazzaMapVista();
                return;
              }
              setMapMenuOpen((o) => !o);
            }}
          >
            <span aria-hidden>🗺️</span>
          </button>

          {!onPiazze && mapMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-[10040] header-nav-quick__scrim"
                aria-hidden
                onClick={() => setMapMenuOpen(false)}
              />
              <div
                className="header-nav-quick__map-menu"
                role="menu"
                data-testid="header-mappa-menu"
              >
                <Link
                  to={primaryHref}
                  role="menuitem"
                  className="header-nav-quick__map-item header-nav-quick__map-item--primary"
                  data-testid="header-mappa-primary"
                  onClick={() => {
                    setMapMenuOpen(false);
                    trackTerritoryEngagement({
                      section: "mappa",
                      source: "header_mobile_primary",
                    });
                  }}
                >
                  <span aria-hidden>🗺️</span>
                  <span>Apri mappa attiva</span>
                </Link>
                {MAP_VARIANTS.map((v) => (
                  <Link
                    key={v.id}
                    to={v.href}
                    role="menuitem"
                    className="header-nav-quick__map-item"
                    data-testid={`header-mappa-${v.id}`}
                    onClick={() => {
                      setMapMenuOpen(false);
                      try {
                        sessionStorage.setItem("6amico_map_esperienza", v.id);
                      } catch {
                        /* ignore */
                      }
                      trackTerritoryEngagement({
                        section: "mappa",
                        source: `header_mobile_${v.id}`,
                      });
                    }}
                  >
                    <span aria-hidden>{v.emoji}</span>
                    <span>{v.label}</span>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        className="header-nav-quick__btn header-nav-quick__btn--desktop"
        data-testid="header-ambasciatore-desktop"
        aria-label="Apri Ambasciatore"
        title="Ambasciatore"
        onClick={openAmbasciatoreChat}
      >
        <MessageSquareQuote size={14} aria-hidden />
        <span>Ambasciatore</span>
      </button>

      <button
        type="button"
        className="header-nav-quick__btn header-nav-quick__btn--mobile"
        data-testid="header-ambasciatore-btn"
        aria-label="Apri Ambasciatore"
        title="Ambasciatore"
        onClick={openAmbasciatoreChat}
      >
        <span aria-hidden>🎖️</span>
      </button>
    </div>
  );
}
